import { supabase } from "../supabaseClient";

const uniqueViolation = (error) =>
  error?.code === "23505" || /duplicate key/i.test(error?.message || "");

export const googlePlaceId = (value) =>
  String(value || "").replace(/^places\//, "");

export const placeDisplayName = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.text || "";
};

export const listOrganizationBowlingCenters = async (organizationId) => {
  const { data, error } = await supabase
    .from("organizationBowlingCenters")
    .select(
      `
      id,
      bowlingCenterId,
      createdAt,
      bowlingCenters (
        id,
        centerName,
        formattedAddress,
        googlePlaceID,
        phone,
        website,
        city,
        stateShort,
        deletedAt
      )
    `
    )
    .eq("organizationId", organizationId)
    .order("createdAt", { ascending: true });

  if (error) throw error;

  return (data || [])
    .filter((row) => row.bowlingCenters && !row.bowlingCenters.deletedAt)
    .sort((a, b) =>
      (a.bowlingCenters.centerName || "").localeCompare(
        b.bowlingCenters.centerName || ""
      )
    );
};

export const findBowlingCenterByPlaceId = async (
  googlePlaceID,
  { includeDeleted = false } = {}
) => {
  const placeId = googlePlaceId(googlePlaceID);
  if (!placeId) return null;

  const { data, error } = await supabase
    .from("bowlingCenters")
    .select("id, googlePlaceID, deletedAt")
    .eq("googlePlaceID", placeId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;
  if (!includeDeleted && data.deletedAt) return null;
  return data;
};

const catalogFieldsFromGoogle = (center) => ({
  centerName: center.centerName,
  formattedAddress: center.formattedAddress || null,
  streetFirstLine: center.streetFirstLine || null,
  streetSecondLine: center.streetSecondLine || null,
  city: center.city || null,
  state: center.state || null,
  stateShort: center.stateShort || null,
  latitude: center.latitude,
  longitude: center.longitude,
  phone: center.phone || null,
  website: center.website || null,
});

export const upsertBowlingCenter = async (center, userId) => {
  const placeId = googlePlaceId(center.googlePlaceID);
  const fields = catalogFieldsFromGoogle(center);
  const now = new Date().toISOString();
  const existing = await findBowlingCenterByPlaceId(placeId, {
    includeDeleted: true,
  });

  if (existing) {
    const { data, error } = await supabase
      .from("bowlingCenters")
      .update({
        ...fields,
        deletedAt: null,
        updatedAt: now,
      })
      .eq("id", existing.id)
      .select("id")
      .single();

    if (error) throw error;
    return { id: data.id };
  }

  const { data, error } = await supabase
    .from("bowlingCenters")
    .insert({
      ...fields,
      googlePlaceID: placeId,
      status: "open",
      createdBy: userId,
      createdAt: now,
      updatedAt: now,
    })
    .select("id")
    .single();

  if (uniqueViolation(error)) {
    const raced = await findBowlingCenterByPlaceId(placeId, {
      includeDeleted: true,
    });
    if (raced) {
      const { data: updated, error: updateError } = await supabase
        .from("bowlingCenters")
        .update({
          ...fields,
          deletedAt: null,
          updatedAt: now,
        })
        .eq("id", raced.id)
        .select("id")
        .single();
      if (updateError) throw updateError;
      return { id: updated.id };
    }
  }
  if (error) throw error;
  return data;
};

export const linkOrganizationBowlingCenter = async ({
  organizationId,
  bowlingCenterId,
  userId,
}) => {
  const { data: existing, error: existingError } = await supabase
    .from("organizationBowlingCenters")
    .select("id")
    .eq("organizationId", organizationId)
    .eq("bowlingCenterId", bowlingCenterId)
    .maybeSingle();

  if (existingError) throw existingError;
  if (existing) return { alreadyLinked: true };

  const { error } = await supabase.from("organizationBowlingCenters").insert({
    organizationId,
    bowlingCenterId,
    createdBy: userId || null,
  });

  if (uniqueViolation(error)) return { alreadyLinked: true };
  if (error) throw error;
  return { alreadyLinked: false };
};

export const unlinkOrganizationBowlingCenter = async ({
  organizationId,
  bowlingCenterId,
}) => {
  const { error } = await supabase
    .from("organizationBowlingCenters")
    .delete()
    .eq("organizationId", organizationId)
    .eq("bowlingCenterId", bowlingCenterId);

  if (error) throw error;
};

export const organizationIdForTournament = async (tournamentId) => {
  const { data, error } = await supabase
    .from("tournaments")
    .select("organizationID")
    .eq("id", tournamentId)
    .single();
  if (error) throw error;
  return data?.organizationID || null;
};

export const listBowlingCentersForTournament = async (tournamentId) => {
  const organizationId = await organizationIdForTournament(tournamentId);
  if (!organizationId) return [];
  return listOrganizationBowlingCenters(organizationId);
};

export const bowlingCenterCityLine = (center) =>
  [center?.city, center?.stateShort || center?.state]
    .filter(Boolean)
    .join(", ");

export const bowlingCenterStreetLine = (center) => {
  const street = center?.streetFirstLine || "";
  const address = center?.formattedAddress || "";
  const cityLine = bowlingCenterCityLine(center);
  if (street && street !== cityLine) return street;
  if (address && address !== cityLine) return address;
  return "";
};

export const bowlingCenterMapsHref = (center) => {
  if (center?.googlePlaceID) {
    return `https://www.google.com/maps/place/?q=place_id:${center.googlePlaceID}`;
  }
  const query =
    center?.formattedAddress ||
    [center?.streetFirstLine, bowlingCenterCityLine(center)]
      .filter(Boolean)
      .join(", ") ||
    center?.centerName;
  if (!query) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
};

export const locationRowsFromCenters = (centers) =>
  (centers || []).map((center) => ({
    id: center.id,
    bowlingCenters: center,
  }));

export const locationRowsFromSquads = (squads) => {
  const byId = new Map();
  for (const squad of squads || []) {
    if (squad.isDeleted) continue;
    const center = squad.bowlingCenters;
    if (center?.id) byId.set(center.id, center);
  }
  return locationRowsFromCenters([...byId.values()]);
};

export const listDistinctSquadBowlingCenters = async (tournamentId) => {
  const { data, error } = await supabase
    .from("squads")
    .select(
      `
      id,
      bowlingCenters (
        id,
        centerName,
        formattedAddress,
        googlePlaceID,
        phone,
        website,
        city,
        state,
        stateShort,
        streetFirstLine
      )
    `
    )
    .eq("tournamentID", tournamentId)
    .eq("isDeleted", false);

  if (error) throw error;

  const byId = new Map();
  for (const row of data || []) {
    const center = row.bowlingCenters;
    if (center?.id) byId.set(center.id, center);
  }

  return [...byId.values()].sort((a, b) =>
    (a.centerName || "").localeCompare(b.centerName || "")
  );
};

